import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { PurchaseResult } from '@revenuecat/purchases-js';

import { logger } from '#lib/logger';
import { ModalSelectComponent } from '#components/modal-select/modal-select.component';
import { ModalSelectOptions } from 'src/app/models/modal/modal-select-options.model';
import { IntroSubscriptionComponent } from '#components/intro-screen/intro-subscription/intro-subscription.component';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  /**
   * A BehaviorSubject that tracks whether a modal is currently open. This can be used by components to
   * prevent multiple modals from being opened simultaneously or to adjust UI behavior when a modal is active.
   */
  modalOpen$ = new BehaviorSubject<boolean>(false);

  constructor(private modalCtrl: ModalController) {}

  /**
   * Opens a modal for selecting items.
   *
   * @template T - The type of the items to select from.
   * @template R - The type of the data returned upon confirmation.
   * @param {ModalSelectOptions<T>} options - The options for configuring the modal.
   * @param {(data: R) => void} nextFn - The callback function to execute with the selected data upon confirmation.
   * @returns {Promise<void>} A promise that resolves when the modal is dismissed.
   */
  async openModalSelect<T, R>(
    options: ModalSelectOptions<T>,
    nextFn: (data: R) => void
  ): Promise<void> {
    const { modalId, multiSelect, title, items, idKey, textKey, descriptionKey, selected } =
      options;

    const modal = await this.modalCtrl.create({
      component: ModalSelectComponent,
      componentProps: {
        modalId,
        multiSelect,
        title,
        items,
        idKey,
        textKey,
        descriptionKey,
        selected,
      },
      backdropDismiss: false,
      canDismiss: async (_, role) => {
        return role === 'confirm' || role === 'cancel';
      },
      presentingElement: document.querySelector('#main-content') as HTMLElement,
      id: modalId,
    });

    // Set the modal open state to true.
    this.setModalOpenState(true);

    await modal.present();

    const { data, role } = await modal.onWillDismiss<R>();

    this.setModalOpenState(false);

    // Execute the callback function with the selected data. Ignore if data is null.
    if (role === 'confirm' && data) {
      nextFn(data);
    }
  }

  /**
   * Opens the introductory subscription modal and handles its dismissal.
   *
   * @param nextFn - A callback function that is invoked with the result data (`PurchaseResult` or `null`)
   *                 when the modal is dismissed.
   * @returns A promise that resolves when the modal has been presented and dismissed.
   */
  async openModalIntroSubscription(nextFn: (data: PurchaseResult | null) => void): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: IntroSubscriptionComponent,
      componentProps: {
        modalId: 'intro-subscription',
      },
      backdropDismiss: false,
      canDismiss: async (_, role) => {
        return role !== undefined;
      },
      id: 'intro-subscription',
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss<PurchaseResult>();

    if (role) {
      // Send even if nothing is returned.
      nextFn(data ?? null);
    }
  }

  private setModalOpenState(state: boolean): void {
    this.modalOpen$.next(state);

    logger.log(state, 'ModalService.setModalOpenState -> state');
  }
}
